/**
 * HO FONT DONG GOI - nang luc moi khien viec sua chu thuc su kha thi.
 *
 * =============================================================================
 *  VI SAO BAT BUOC PHAI CO
 * =============================================================================
 *  Do tren 284 tai lieu that cua nguoi dung:
 *      - chi 11.5% font subset nhung san du glyph de go lai tieng Viet
 *      - do phu tieng Viet cua font subset co TRUNG VI 0%
 *      - 74 tai lieu bi fsType cam dung font nhung san
 *  Nghia la trong da so truong hop, font trong file KHONG DUNG LAI DUOC. Neu
 *  khong co font du phong, cau tra loi duy nhat la "khong sua duoc" - va do
 *  chinh la trai nghiem ma nguoi dung vua gap.
 *
 * =============================================================================
 *  VI SAO LA FONT DONG GOI CHU KHONG PHAI FONT HE THONG
 * =============================================================================
 *  Ban thu nghiem tren Node quet C:\Windows\Fonts. Trong trinh duyet dieu do
 *  bat kha thi, va quan trong hon: PHAN PHOI LAI font he thong Windows (Arial,
 *  Segoe UI, Times New Roman) LA VI PHAM GIAY PHEP FONT. Rui ro phap ly nay
 *  RIENG BIET voi giay phep thu vien PDF.
 *
 *  Ba ho font dong goi (Noto Sans, Noto Serif, Be Vietnam Pro) deu theo giay
 *  phep OFL-1.1 (cac tep giay phep nam canh file font tai public/fonts/) cho
 *  phep CA nhung CA phan phoi lai. OFL-1.1 phu CA BON mat chu cua moi ho font.
 *  Danh sach ho font la MOT NGUON DUY NHAT trong families.ts - xem tep do.
 *
 * =============================================================================
 *  VI SAO LA BON TEP CHU KHONG PHAI MOT (moi ho font)
 * =============================================================================
 *  Nguoi dung bao "chữ đậm và nghiêng không có tác dụng". Truoc thay doi do,
 *  hai nut do ghi `bold` / `italic` vao doi tuong va KHONG MOT DOAN MA NAO doc
 *  chung. Cach chua: MAT CHU THAT - bon tep font that su cho moi ho, khong phai
 *  hieu ung to vien / truot hinh gia lam dam nghieng (chung day dau tieng Viet
 *  ra khoi than chu).
 *
 * =============================================================================
 *  QUY TAC: TAI MOT LAN, VA CHI TAI (HO, MAT CHU) DUOC DUNG
 * =============================================================================
 *  Moi tep font nang khoang 130KB-780KB. Tai het 12 mat chu ngay khi mo tai lieu
 *  la bat nguoi dung tra vai megabyte cho nhung phong phan lon ho khong bao gio
 *  bam toi. Nen bo nho dem o day tach RIENG cho tung CAP (ho, mat chu): chi cap
 *  nao that su duoc dung moi duoc tai ve, va tai xong thi giu lai ca ArrayBuffer
 *  LAN doi tuong fontkit. Lesson so 5: mot tai lieu chi dung Noto Sans khong duoc
 *  mang theo byte cua Noto Serif.
 */
import type { Font } from './fontkit';
import { fontFromBytes } from './fontkit';
import { checkCoverage, type CoverageResult } from './coverage';
import {
  DEFAULT_FONT_FAMILY_ID,
  fontFileUrl,
  getFontFamily,
  normalizeFontFamilyId,
  type FontFamily,
  type FontFamilyId,
} from './families';

// Lo lai danh ba ho font qua day de ben goi chi phai nho MOT duong nhap font.
export {
  DEFAULT_FONT_FAMILY_ID,
  FONT_FAMILIES,
  fontFileUrl,
  getFontFamily,
  isFontFamilyId,
  normalizeFontFamilyId,
  type FontFamily,
  type FontFamilyId,
} from './families';

/** Bon mat chu cua mot ho font. Khoa nay di xuyen suot ca duong xem lan duong ghi. */
export type FontStyleKey = 'regular' | 'bold' | 'italic' | 'boldItalic';

/** Kieu chu ma mot doi tuong yeu cau. Ca hai truong deu tuy chon = Regular. */
export interface TextFaceStyle {
  bold?: boolean | undefined;
  italic?: boolean | undefined;
}

/** Doi mot cap co/khong thanh khoa mat chu. MOT cho duy nhat lam phep doi nay. */
export function fontStyleKey(style: TextFaceStyle | null | undefined): FontStyleKey {
  const bold = style?.bold === true;
  const italic = style?.italic === true;
  if (bold && italic) return 'boldItalic';
  if (bold) return 'bold';
  if (italic) return 'italic';
  return 'regular';
}

export const FONT_STYLE_KEYS: readonly FontStyleKey[] = [
  'regular',
  'bold',
  'italic',
  'boldItalic',
];

/**
 * Duong dan cong khai cua tung mat chu cho HO MAC DINH (Noto Sans). Giu lai vi
 * cac duong goi cu tham chieu ten nay; ho font khac lay duong dan qua families.
 */
export const BUNDLED_FONT_URLS: Readonly<Record<FontStyleKey, string>> = faceUrls(
  DEFAULT_FONT_FAMILY_ID,
);

/** Ten tep cua tung mat chu HO MAC DINH - de ban desktop va bo test tim tren o dia. */
export const BUNDLED_FONT_FILES: Readonly<Record<FontStyleKey, string>> = {
  regular: getFontFamily(DEFAULT_FONT_FAMILY_ID).files.regular,
  bold: getFontFamily(DEFAULT_FONT_FAMILY_ID).files.bold,
  italic: getFontFamily(DEFAULT_FONT_FAMILY_ID).files.italic,
  boldItalic: getFontFamily(DEFAULT_FONT_FAMILY_ID).files.boldItalic,
};

/** Duong dan cua ban Regular ho mac dinh. Giu lai vi ban goi cu tham chieu ten nay. */
export const BUNDLED_FONT_URL = BUNDLED_FONT_URLS.regular;

function faceUrls(familyId: FontFamilyId): Readonly<Record<FontStyleKey, string>> {
  const files = getFontFamily(familyId).files;
  return {
    regular: fontFileUrl(files.regular),
    bold: fontFileUrl(files.bold),
    italic: fontFileUrl(files.italic),
    boldItalic: fontFileUrl(files.boldItalic),
  };
}

export interface BundledFont {
  /** Byte font. Truyen thang cho PDFDocument.embedFont(). */
  bytes: Uint8Array;
  font: Font;
  /** Ho font ma ban ghi nay thuoc ve. */
  familyId: FontFamilyId;
  /** Mat chu ma ban ghi nay dai dien. */
  style: FontStyleKey;
  postscriptName: string;
  familyName: string;
  numGlyphs: number;
  /** Ghi cong giay phep - giao dien nen hien duoc o muc "Ve chuong trinh". */
  licence: 'OFL-1.1';
}

/** Khoa bo nho dem: mot cap (ho font, mat chu). */
function cacheKey(familyId: FontFamilyId, style: FontStyleKey): string {
  return `${familyId}:${style}`;
}

const cached = new Map<string, BundledFont>();
const inFlight = new Map<string, Promise<BundledFont | null>>();
const listeners = new Set<() => void>();

/**
 * Bao cho ben goi biet vua co mot mat chu duoc nap xong.
 *
 * Lop hien thi ve theo mat chu DANG CO. Neu no nap mot mat chu moi ma khong ai
 * bao no ve lai, hop chu se dung mai bang phep do cua mat chu du phong cho toi
 * lan ve ke tiep vi mot ly do khac - tuc la chu dam / doi phong nhin khong doi.
 * Do la bai hoc so 3 o dang tinh vi nhat: hai ben khong lech vi cong thuc, ma vi
 * thoi diem.
 */
export function onBundledFontLoaded(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function announce(): void {
  for (const listener of [...listeners]) {
    try {
      listener();
    } catch {
      // Mot ben nghe hong khong duoc keo theo cac ben con lai.
    }
  }
}

function record(
  familyId: FontFamilyId,
  style: FontStyleKey,
  bytes: Uint8Array,
  font: Font,
): BundledFont {
  const family: FontFamily = getFontFamily(familyId);
  const fallbackName = family.files[style].replace(/\.ttf$/i, '');
  const entry: BundledFont = {
    bytes,
    font,
    familyId,
    style,
    postscriptName: font.postscriptName ?? fallbackName,
    familyName: font.familyName ?? family.label,
    numGlyphs: font.numGlyphs ?? 0,
    licence: 'OFL-1.1',
  };
  cached.set(cacheKey(familyId, style), entry);
  return entry;
}

/**
 * Nap MOT mat chu cua MOT ho font. Tra ve null khi khong nap duoc (thieu file,
 * mang loi, byte hong) - ben goi PHAI xu ly nhu "khong co mat chu nay" chu khong
 * duoc gia dinh la co.
 */
export function loadBundledFamilyStyle(
  familyId: FontFamilyId = DEFAULT_FONT_FAMILY_ID,
  style: FontStyleKey = 'regular',
): Promise<BundledFont | null> {
  const key = cacheKey(familyId, style);
  const ready = cached.get(key);
  if (ready) return Promise.resolve(ready);
  const running = inFlight.get(key);
  if (running) return running;

  const url = faceUrls(familyId)[style];
  const task = (async (): Promise<BundledFont | null> => {
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const bytes = new Uint8Array(await res.arrayBuffer());
      const font = fontFromBytes(bytes);
      if (!font) return null;
      const entry = record(familyId, style, bytes, font);
      announce();
      return entry;
    } catch {
      return null;
    } finally {
      // Xoa co gang dang chay du thanh hay bai: mot lan mang hong khong duoc
      // khoa vinh vien kha nang sua cua ca phien lam viec.
      inFlight.delete(key);
    }
  })();

  inFlight.set(key, task);
  return task;
}

/** Nap mat chu cua mot ho font theo cap dam/nghieng. */
export function loadBundledFamilyFace(
  familyId: FontFamilyId,
  style: TextFaceStyle,
): Promise<BundledFont | null> {
  return loadBundledFamilyStyle(familyId, fontStyleKey(style));
}

/**
 * Nap MOT mat chu ho MAC DINH. Diem vao cu, giu nguyen hanh vi.
 */
export function loadBundledFontStyle(
  style: FontStyleKey = 'regular',
): Promise<BundledFont | null> {
  return loadBundledFamilyStyle(DEFAULT_FONT_FAMILY_ID, style);
}

/** Nap mat chu ho MAC DINH theo cap dam/nghieng. Duong goi tu nhien cua tang giao dien. */
export function loadBundledFace(style: TextFaceStyle): Promise<BundledFont | null> {
  return loadBundledFamilyStyle(DEFAULT_FONT_FAMILY_ID, fontStyleKey(style));
}

/**
 * Nap font dong goi (ban Regular ho mac dinh).
 *
 * Diem vao cu, giu nguyen hanh vi: ban Regular la thu ca ung dung can co truoc
 * khi lam duoc bat cu viec gi voi chu, nen no van la cau hoi "co font khong".
 */
export function loadBundledFont(): Promise<BundledFont | null> {
  return loadBundledFamilyStyle(DEFAULT_FONT_FAMILY_ID, 'regular');
}

/**
 * Nap san byte font tu ben ngoai.
 *
 * Ton tai vi hai ly do that: (1) test chay tren Node khong co fetch() toi mot
 * may chu Vite; (2) ban desktop co the doc font tu o dia. Khong phai cua sau
 * cho test - la diem cam nguon font. Bo qua `familyId` = ho mac dinh.
 */
export function primeBundledFont(
  bytes: Uint8Array,
  style: FontStyleKey = 'regular',
  familyId: FontFamilyId = DEFAULT_FONT_FAMILY_ID,
): BundledFont | null {
  const font = fontFromBytes(bytes);
  if (!font) return null;
  const entry = record(familyId, style, bytes, font);
  announce();
  return entry;
}

/** Xoa bo nho dem cua MOI ho font. Chi dung trong test. */
export function resetBundledFont(): void {
  cached.clear();
  inFlight.clear();
}

/** Ban da nap roi hay chua (ho mac dinh), khong kich hoat viec nap. */
export function peekBundledFont(style: FontStyleKey = 'regular'): BundledFont | null {
  return cached.get(cacheKey(DEFAULT_FONT_FAMILY_ID, style)) ?? null;
}

/** Ban da nap roi hay chua cua MOT ho font, khong kich hoat viec nap. */
export function peekBundledFamilyFont(
  familyId: FontFamilyId,
  style: FontStyleKey = 'regular',
): BundledFont | null {
  return cached.get(cacheKey(familyId, style)) ?? null;
}

/**
 * MAT CHU DE DO NGAY BAY GIO cua mot ho font, khong cho.
 *
 * Tra ve mat chu duoc yeu cau neu no da nam trong bo nho dem; neu chua thi kich
 * hoat viec nap VA lui ve ban Regular CUNG HO de con ve duoc mot thu gi do ngay
 * lap tuc. Chua co ca Regular cung ho thi lui tiep ve Regular ho mac dinh. Khi
 * mat chu that nap xong, onBundledFontLoaded() bao cho lop hien thi ve lai.
 *
 * Tra ve null chi khi ngay ca Regular ho mac dinh cung chua co - luc do khong con
 * gi de do.
 */
export function peekBundledFamilyFace(
  familyId: FontFamilyId,
  style: TextFaceStyle,
): BundledFont | null {
  const normalized = normalizeFontFamilyId(familyId);
  const key = fontStyleKey(style);
  const exact = cached.get(cacheKey(normalized, key));
  if (exact) return exact;

  // Chua co dung mat chu: kich hoat nap va lui ve Regular cung ho.
  if (key !== 'regular') void loadBundledFamilyStyle(normalized, key);
  const familyRegular = cached.get(cacheKey(normalized, 'regular'));
  if (familyRegular) return familyRegular;

  // Chua co ca Regular cung ho: kich hoat nap no, va lui tiep ve ho mac dinh.
  if (normalized !== DEFAULT_FONT_FAMILY_ID) void loadBundledFamilyStyle(normalized, 'regular');
  return cached.get(cacheKey(DEFAULT_FONT_FAMILY_ID, 'regular')) ?? null;
}

/**
 * MAT CHU DE DO NGAY BAY GIO (ho mac dinh), khong cho. Diem vao cu, giu nguyen.
 */
export function peekBundledFace(style: TextFaceStyle): BundledFont | null {
  return peekBundledFamilyFace(DEFAULT_FONT_FAMILY_ID, style);
}

/**
 * Font dong goi co phu duoc chuoi nay khong.
 *
 * Cau tra loi phai lay tu CHUONG TRINH FONT chu khong tu gia dinh - va tu chuong
 * trinh font CUA DUNG (HO, MAT CHU) SE DUOC GHI RA, khong phai cua ban Regular ho
 * mac dinh (bai hoc so 6).
 */
export async function bundledCoverage(
  text: string,
  style: TextFaceStyle = {},
  familyId: FontFamilyId = DEFAULT_FONT_FAMILY_ID,
): Promise<CoverageResult | null> {
  const bundled = await loadBundledFamilyStyle(familyId, fontStyleKey(style));
  if (!bundled) return null;
  return checkCoverage(bundled.font, text);
}
